import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import PageBanner from '../components/PageBanner';
import SEO from '../components/SEO';
import API from '../api';
import { useLanguage } from '../context/LanguageContext';

const BASE = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api', '') : 'http://localhost:5000';
const TABS = ['all', 'highlight', 'announcement', 'event'];

function formatDate(str, language) {
  if (!str) return '';
  return new Date(str).toLocaleDateString(language === 'ja' ? 'ja-JP' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function Promotions() {
  const { t, language } = useLanguage();
  const [tab, setTab] = useState('all');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    API.get('/promotions', { params: tab === 'all' ? {} : { category: tab } })
      .then(r => setPosts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [tab]);

  const imgSrc = (url) => url?.startsWith('/') ? `${BASE}${url}` : url;
  const [featured, ...rest] = posts;

  return (
    <PageLayout>
      <SEO title={t('promotions.title')} description="News, announcements, and events from Sankyo Machine Tools Philippines Inc." />
      <PageBanner en="NEWS" title={t('promotions.title')} breadcrumbs={[{ label: t('promotions.title') }]} />

      <section className="pt-6 pb-20 lg:pb-28 bg-white">
        <div className="w-[90%] max-w-5xl mx-auto">
          {/* Category tabs */}
          <div className="flex flex-wrap gap-2 justify-center mb-12 border-b border-gray-100 pb-6">
            {TABS.map(key => (
              <button key={key} onClick={() => setTab(key)}
                className={`text-xs font-semibold uppercase tracking-widest px-4 py-2 transition-colors ${tab === key
                  ? 'bg-primary text-white'
                  : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}>
                {t(`promotions.tab_${key}`)}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="space-y-6">
              <div className="h-80 bg-gray-100 animate-pulse" />
              {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-28 bg-gray-100 animate-pulse" />)}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-400 text-lg">{t('promotions.empty')}</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Featured (most recent) */}
              <Link to={`/promotional/${featured.slug}`} className="group block fade-up">
                <div className="aspect-[16/9] bg-gray-100 overflow-hidden mb-4">
                  {featured.image_url ? (
                    <img src={imgSrc(featured.image_url)} alt={featured.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-gray-100">
                      <span className="text-primary/30 text-6xl font-display font-bold">{featured.title[0]}</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400 mb-1.5">{formatDate(featured.published_at, language)}</p>
                <h2 className="text-2xl font-bold text-ink group-hover:text-primary transition-colors">{featured.title}</h2>
                {featured.excerpt && <p className="text-gray-500 text-sm mt-2">{featured.excerpt}</p>}
              </Link>

              {/* Rest, as a list */}
              {rest.length > 0 && (
                <div className="divide-y divide-gray-100 border-t border-gray-100 pt-2">
                  {rest.map((post, i) => (
                    <Link key={post.id} to={`/promotional/${post.slug}`}
                      className="group flex items-center gap-5 py-5 fade-up" style={{ animationDelay: `${i * 0.06}s` }}>
                      <div className="w-32 sm:w-44 aspect-[16/9] bg-gray-100 overflow-hidden shrink-0">
                        {post.image_url ? (
                          <img src={imgSrc(post.image_url)} alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-gray-100">
                            <span className="text-primary/30 text-2xl font-display font-bold">{post.title[0]}</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 mb-1">{formatDate(post.published_at, language)}</p>
                        <h3 className="font-bold text-ink group-hover:text-primary transition-colors">{post.title}</h3>
                        {post.excerpt && <p className="text-gray-500 text-xs mt-1 line-clamp-1">{post.excerpt}</p>}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </PageLayout>
  );
}
